/**
 * Detect which ontology facts are missing for an indeterminate violation so
 * the UI can prompt the architect to supply them.
 *
 * Covers predicate measurements, rule scope (EERO / means of egress), and
 * exception / special-case `appliesIf` conditions — not just the main threshold.
 */
import {
  type EvaluatedElement,
  ontologyPropertyToFactKey,
  type RulePredicate,
  type RuleSpec,
  type UnitRef,
} from "@vera/rules";
import type { Violation } from "@vera/shared";
import type { UserFactValue } from "../stores/userFactStore";

export interface MissingFactField {
  factKey: string;
  ontologyProperty: string;
  label: string;
  unit: UnitRef;
  kind: "quantity" | "boolean" | "enum";
  /** When the fact gates scope or an exception branch */
  context?: string;
  enumOptions?: string[];
  /** Already saved by the architect — show pre-filled, still editable */
  alreadySupplied?: boolean;
}

const FACT_LABELS: Record<string, string> = {
  openingHeight: "Net clear opening height",
  netClearOpeningArea: "Net clear opening area",
  sillHeight: "Sill height above floor",
  glazingAreaRatio: "Glazing area (% of floor area)",
  openableAreaRatio: "Openable area (% of floor area)",
  clearWidth: "Clear width",
  riserHeight: "Riser height",
  treadDepth: "Tread depth",
  verticalRise: "Total vertical rise",
  headroom: "Headroom clearance",
  floorArea: "Floor area",
  ceilingHeight: "Ceiling height",
  handrailHeight: "Handrail height",
  guardHeight: "Guard height",
  mechanicalVentilationACH: "Mechanical ventilation (ACH)",
  mechanicalVentilationAch: "Mechanical ventilation (ACH)",
  localExhaust: "Local exhaust system installed",
  averageIlluminanceLux: "Average illuminance (lux)",
  exteriorSunroomWallOpenRatio: "Exterior sunroom wall open area (%)",
  areExteriorSunroomWallsEnclosedOnlyByInsectScreening:
    "Sunroom walls enclosed only by insect screening",
  isRequiredEero:
    "Is this the designated emergency escape & rescue opening (EERO)?",
  isOnRequiredMeansOfEgress: "Is this on a required means of egress?",
  hasSlopedCeiling: "Does the room have a sloped ceiling?",
  hasRoomUse: "Room use / type",
  roomUse: "Room use / type",
  containsHabitableSpace: "Does the space contain habitable area?",
  occupancyClass: "Occupancy class",
  isForBedMovement: "Is the door used for bed movement?",
  hasCeilingHeightAboveFixture:
    "Ceiling height above fixture (shower / tub area)",
};

/** Unit + kind hints for facts not driven by a RulePredicate. */
const FACT_META: Record<
  string,
  Pick<MissingFactField, "unit" | "kind" | "label">
> = {
  glazingAreaRatio: {
    unit: "unitless",
    kind: "quantity",
    label: "Glazing area (% of floor area)",
  },
  openableAreaRatio: {
    unit: "unitless",
    kind: "quantity",
    label: "Openable area (% of floor area)",
  },
  mechanicalVentilationACH: {
    unit: "unitless",
    kind: "quantity",
    label: "Mechanical ventilation (ACH)",
  },
  mechanicalVentilationAch: {
    unit: "unitless",
    kind: "quantity",
    label: "Mechanical ventilation (ACH)",
  },
  averageIlluminanceLux: {
    unit: "unitless",
    kind: "quantity",
    label: "Average illuminance (lux)",
  },
  exteriorSunroomWallOpenRatio: {
    unit: "unitless",
    kind: "quantity",
    label: "Exterior sunroom wall open area (%)",
  },
  roomUse: { unit: "unitless", kind: "enum", label: "Room use / type" },
  hasRoomUse: { unit: "unitless", kind: "enum", label: "Room use / type" },
};

function labelFor(factKey: string): string {
  return (
    FACT_LABELS[factKey] ??
    factKey.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase())
  );
}

export function factPresent(
  facts: EvaluatedElement["facts"],
  factKey: string
): boolean {
  const v = facts[factKey];
  if (v === undefined) return false;
  if (typeof v === "object" && v !== null && "value" in v && "unit" in v) {
    return Number.isFinite((v as { value: number }).value);
  }
  return true;
}

function fieldFromPredicate(pred: RulePredicate): MissingFactField {
  const factKey = ontologyPropertyToFactKey(pred.property);
  const meta = FACT_META[factKey];
  return {
    factKey,
    ontologyProperty: pred.property,
    label: meta?.label ?? labelFor(factKey),
    unit: pred.unit,
    kind: meta?.kind ?? "quantity",
  };
}

function fieldFromAppliesIf(
  iri: string,
  expected: unknown,
  context: string
): MissingFactField {
  const factKey = ontologyPropertyToFactKey(iri);
  const meta = FACT_META[factKey];
  if (typeof expected === "boolean") {
    return {
      factKey,
      ontologyProperty: iri,
      label: meta?.label ?? labelFor(factKey),
      unit: "unitless",
      kind: "boolean",
      context,
    };
  }
  if (Array.isArray(expected) && expected.every(x => typeof x === "string")) {
    return {
      factKey,
      ontologyProperty: iri,
      label: meta?.label ?? labelFor(factKey),
      unit: "unitless",
      kind: "enum",
      enumOptions: expected as string[],
      context,
    };
  }
  if (typeof expected === "string") {
    return {
      factKey,
      ontologyProperty: iri,
      label: meta?.label ?? labelFor(factKey),
      unit: "unitless",
      kind: "enum",
      enumOptions: [expected],
      context,
    };
  }
  if (typeof expected === "number") {
    return {
      factKey,
      ontologyProperty: iri,
      label: meta?.label ?? labelFor(factKey),
      unit: meta?.unit ?? "unitless",
      kind: "quantity",
      context,
    };
  }
  return {
    factKey,
    ontologyProperty: iri,
    label: meta?.label ?? labelFor(factKey),
    unit: meta?.unit ?? "millimetre",
    kind: meta?.kind ?? "quantity",
    context,
  };
}

function missingFromAppliesIf(
  appliesIf: Record<string, unknown>,
  facts: EvaluatedElement["facts"],
  context: string
): MissingFactField[] {
  const out: MissingFactField[] = [];
  for (const [iri, expected] of Object.entries(appliesIf)) {
    const factKey = ontologyPropertyToFactKey(iri);
    if (factPresent(facts, factKey)) continue;
    out.push(fieldFromAppliesIf(iri, expected, context));
  }
  return out;
}

/** Scope facts the engine needs before it can confirm a rule applies. */
function scopeMissingFacts(
  rule: RuleSpec,
  facts: EvaluatedElement["facts"]
): MissingFactField[] {
  if (rule.appliesTo.scope !== "means_of_egress") return [];
  const elClass = rule.appliesTo.elementClass;
  if (elClass === "vera:Opening" || elClass === "vera:Window") {
    if (factPresent(facts, "isRequiredEero")) return [];
    return [
      {
        factKey: "isRequiredEero",
        ontologyProperty: "vera:isRequiredEero",
        label: labelFor("isRequiredEero"),
        unit: "unitless",
        kind: "boolean",
        context: "Rule scope — only designated EERO openings are checked",
      },
    ];
  }
  if (factPresent(facts, "isOnRequiredMeansOfEgress")) return [];
  return [
    {
      factKey: "isOnRequiredMeansOfEgress",
      ontologyProperty: "vera:isOnRequiredMeansOfEgress",
      label: labelFor("isOnRequiredMeansOfEgress"),
      unit: "unitless",
      kind: "boolean",
      context: "Rule scope — means of egress",
    },
  ];
}

function predicatesForRule(rule: RuleSpec): RulePredicate[] {
  const preds = [rule.predicate];
  if (rule.conjunctivePredicates?.length) {
    preds.push(...rule.conjunctivePredicates);
  }
  for (const sc of rule.specialCases ?? []) {
    preds.push(sc.predicate);
  }
  for (const ex of rule.exceptions ?? []) {
    if (ex.substitutePredicate) preds.push(ex.substitutePredicate);
  }
  return preds;
}

function dedupeFields(fields: MissingFactField[]): MissingFactField[] {
  const seen = new Set<string>();
  const out: MissingFactField[] = [];
  for (const f of fields) {
    if (seen.has(f.factKey)) continue;
    seen.add(f.factKey);
    out.push(f);
  }
  return out;
}

function missingPredicateFacts(
  rule: RuleSpec,
  facts: EvaluatedElement["facts"]
): MissingFactField[] {
  const out: MissingFactField[] = [];
  for (const pred of predicatesForRule(rule)) {
    const field = fieldFromPredicate(pred);
    if (!factPresent(facts, field.factKey)) out.push(field);
  }
  out.push(...scopeMissingFacts(rule, facts));
  for (const sc of rule.specialCases ?? []) {
    if (Object.keys(sc.appliesIf).length > 0) continue;
    const field = fieldFromPredicate(sc.predicate);
    if (!factPresent(facts, field.factKey)) out.push(field);
  }
  return dedupeFields(out);
}

function missingExceptionFacts(
  rule: RuleSpec,
  facts: EvaluatedElement["facts"],
  exceptionIds: string[]
): MissingFactField[] {
  const idSet = new Set(exceptionIds);
  const out: MissingFactField[] = [];
  for (const ex of rule.exceptions ?? []) {
    if (!idSet.has(ex.id)) continue;
    out.push(
      ...missingFromAppliesIf(
        ex.appliesIf,
        facts,
        `Exception ${ex.id}: ${ex.summary.slice(0, 55)}${ex.summary.length > 55 ? "…" : ""}`
      )
    );
  }
  return dedupeFields(out);
}

/** Measurements pass but exceptions/scope still block a final clear/comply. */
export function isAmbientIndeterminate(violation: Violation): boolean {
  if (violation.provenance?.status !== "indeterminate") return false;
  const summary = violation.summary ?? "";
  const rationale = violation.rationale ?? "";
  return (
    summary.includes("measurement passes") ||
    summary.includes("applicability is unknown") ||
    rationale.includes("would satisfy") ||
    (violation.provenance.observed != null &&
      violation.provenance.threshold != null)
  );
}

function indeterminateExceptionIds(
  violation: Violation,
  /** Prompt one exception branch at a time — avoids dumping every path on the user. */
  limit = 1
): string[] {
  const ids =
    violation.provenance?.exceptionsConsidered
      ?.filter(e => e.decision === "indeterminate")
      .map(e => e.exceptionId) ?? [];
  if (limit <= 0) return ids;
  return ids.slice(0, limit);
}

export interface MissingFactsOptions {
  /** When true, ask every indeterminate exception path at once (advanced). */
  allExceptionPaths?: boolean;
}

/** All askable missing facts for this rule × element facts bag. */
export function missingFactsForEvaluation(
  rule: RuleSpec,
  facts: EvaluatedElement["facts"],
  violation?: Violation,
  options?: MissingFactsOptions
): MissingFactField[] {
  const predicateMissing = missingPredicateFacts(rule, facts);
  if (predicateMissing.length > 0) return predicateMissing;

  if (violation) {
    const exLimit = options?.allExceptionPaths ? 0 : 1;
    const exIds = indeterminateExceptionIds(violation, exLimit);
    if (exIds.length > 0) {
      return missingExceptionFacts(rule, facts, exIds);
    }
  }

  const out: MissingFactField[] = [];
  for (const sc of rule.specialCases ?? []) {
    if (Object.keys(sc.appliesIf).length === 0) continue;
    out.push(
      ...missingFromAppliesIf(
        sc.appliesIf,
        facts,
        `Special case: ${sc.summary.slice(0, 60)}${sc.summary.length > 60 ? "…" : ""}`
      )
    );
  }
  for (const ex of rule.exceptions ?? []) {
    out.push(
      ...missingFromAppliesIf(
        ex.appliesIf,
        facts,
        `Exception: ${ex.summary.slice(0, 60)}${ex.summary.length > 60 ? "…" : ""}`
      )
    );
  }
  return dedupeFields(out);
}

/** Every field this rule may ever ask for (for edit / pre-fill). */
export function allFieldsForRule(rule: RuleSpec): MissingFactField[] {
  const emptyFacts: EvaluatedElement["facts"] = {};
  const out: MissingFactField[] = [];

  for (const pred of predicatesForRule(rule)) {
    out.push(fieldFromPredicate(pred));
  }

  out.push(...scopeMissingFacts(rule, emptyFacts));

  for (const sc of rule.specialCases ?? []) {
    out.push(
      ...missingFromAppliesIf(
        sc.appliesIf,
        emptyFacts,
        `Special case: ${sc.summary.slice(0, 60)}${sc.summary.length > 60 ? "…" : ""}`
      )
    );
  }

  for (const ex of rule.exceptions ?? []) {
    out.push(
      ...missingFromAppliesIf(
        ex.appliesIf,
        emptyFacts,
        `Exception: ${ex.summary.slice(0, 60)}${ex.summary.length > 60 ? "…" : ""}`
      )
    );
  }

  return dedupeFields(out);
}

/** Fields to show when editing architect-supplied values on a user-verified row. */
export function editFieldsForUserSupplied(
  rule: RuleSpec,
  suppliedFactKeys: string[],
  provenanceFacts?: Array<{ factKey: string; label?: string }>
): MissingFactField[] {
  const catalog = allFieldsForRule(rule);
  const byKey = new Map(catalog.map(f => [f.factKey, f]));
  const out: MissingFactField[] = [];
  for (const key of suppliedFactKeys) {
    const fromCatalog = byKey.get(key);
    if (fromCatalog) {
      out.push({ ...fromCatalog, alreadySupplied: true });
      continue;
    }
    const prov = provenanceFacts?.find(f => f.factKey === key);
    const meta = FACT_META[key];
    out.push({
      factKey: key,
      ontologyProperty: `vera:has${key.charAt(0).toUpperCase()}${key.slice(1)}`,
      label: prov?.label ?? meta?.label ?? labelFor(key),
      unit: meta?.unit ?? "unitless",
      kind: meta?.kind ?? "quantity",
      alreadySupplied: true,
    });
  }
  return out;
}

function userSuppliedFactKeys(
  userOverrides: Record<string, UserFactValue> | undefined,
  provenanceFacts?: Array<{ factKey: string }>
): Set<string> {
  const keys = new Set<string>();
  for (const k of Object.keys(userOverrides ?? {})) keys.add(k);
  for (const f of provenanceFacts ?? []) keys.add(f.factKey);
  return keys;
}

/**
 * Form fields = already-supplied (pre-filled) + still-missing.
 * Avoids hiding saved values when the row is still indeterminate.
 */
export function promptFieldsForViolation(
  violation: Violation,
  rule: RuleSpec | undefined,
  element: EvaluatedElement | undefined,
  userOverrides: Record<string, UserFactValue> | undefined,
  options?: MissingFactsOptions
): MissingFactField[] {
  if (!rule || !element) {
    return missingFactsForViolation(violation, rule, element, userOverrides);
  }

  const merged: EvaluatedElement["facts"] = { ...element.facts };
  if (userOverrides) {
    for (const [k, v] of Object.entries(userOverrides)) {
      merged[k] = v;
    }
  }

  const missing = missingFactsForEvaluation(rule, merged, violation, options);
  const suppliedKeys = userSuppliedFactKeys(
    userOverrides,
    violation.provenance?.userSupplied?.facts
  );
  const catalog = allFieldsForRule(rule);
  const byKey = new Map(catalog.map(f => [f.factKey, f]));

  const suppliedFields: MissingFactField[] = [];
  for (const key of suppliedKeys) {
    if (!factPresent(merged, key)) continue;
    if (missing.some(m => m.factKey === key)) continue;
    const fromCatalog = byKey.get(key);
    if (!fromCatalog) continue;
    suppliedFields.push({ ...fromCatalog, alreadySupplied: true });
  }

  const missingMarked = missing.map(m => ({
    ...m,
    alreadySupplied:
      suppliedKeys.has(m.factKey) && factPresent(merged, m.factKey),
  }));

  return dedupeFields([...suppliedFields, ...missingMarked]);
}

/** Parse `vera:hasFoo` from engine indeterminate rationale as fallback. */
export function parseMissingPropertyFromRationale(
  rationale: string | undefined
): string | undefined {
  if (!rationale) return undefined;
  const m = rationale.match(/property "([^"]+)"/);
  return m?.[1];
}

export function missingFactsForViolation(
  violation: Violation,
  rule: RuleSpec | undefined,
  element: EvaluatedElement | undefined,
  userOverrides: Record<string, UserFactValue> | undefined,
  options?: MissingFactsOptions
): MissingFactField[] {
  if (violation.provenance?.status !== "indeterminate") return [];
  if (!rule || !element) {
    const iri = parseMissingPropertyFromRationale(violation.rationale);
    if (!iri) return [];
    const factKey = ontologyPropertyToFactKey(iri);
    if (userOverrides?.[factKey] !== undefined) return [];
    return [
      {
        factKey,
        ontologyProperty: iri,
        label: labelFor(factKey),
        unit: "millimetre",
        kind: "quantity",
      },
    ];
  }

  const merged: EvaluatedElement["facts"] = { ...element.facts };
  if (userOverrides) {
    for (const [k, v] of Object.entries(userOverrides)) {
      merged[k] = v;
    }
  }
  return missingFactsForEvaluation(rule, merged, violation, options);
}
